import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { api, API } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { PageIntro, Empty } from "@/components/PageIntro";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, MapPin, Users, Eye, EyeOff, KeyRound } from "lucide-react";
import { toast } from "sonner";

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const emptyHub = { name: "", code: "", manager: "", location: "" };

const roleBadge = (r) => {
  const map = {
    admin: "bg-primary/10 text-primary border-primary/20",
    manager: "bg-amber-500/10 text-amber-500 border-amber-500/20",
    staff: "bg-cyan-500/10 text-cyan-500 border-cyan-500/20",
  };
  return `text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border font-bold ${map[r] || "bg-muted text-muted-foreground border-border"}`;
};

export default function SettingsPage() {
  const { hubs, loadHubs, companies } = useApp();
  const { user: me } = useAuth();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(emptyHub);

  const openCreate = () => { setEditing(null); setF(emptyHub); setOpen(true); };
  const openEdit = (h) => { setEditing(h.id); setF({ name: h.name || "", code: h.code || "", manager: h.manager || "", location: h.location || "" }); setOpen(true); };

  const submit = async () => {
    if (!f.name || !f.code) return toast.error("Name and code are required");
    try {
      if (editing) { await api.put(`/hubs/${editing}`, f); toast.success("Hub updated"); }
      else { await api.post("/hubs", f); toast.success("Hub added"); }
      setOpen(false); setEditing(null); setF(emptyHub); loadHubs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save hub"); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this hub? Records already tagged to it will remain in the database but won't show a hub name.")) return;
    await api.delete(`/hubs/${id}`);
    toast.success("Hub removed"); loadHubs();
  };

  // ---- Team / Staff logins ----
  const emptyUser = { name: "", email: "", password: "", role: "staff", company_id: "", hub_ids: [] };
  const [users, setUsers] = useState([]);
  const [uOpen, setUOpen] = useState(false);
  const [uf, setUf] = useState(emptyUser);
  const [showPw, setShowPw] = useState(false);
  const [savingU, setSavingU] = useState(false);
  const [uEditing, setUEditing] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [showRpw, setShowRpw] = useState(false);
  const [savingR, setSavingR] = useState(false);
  const [dialogHubs, setDialogHubs] = useState([]);
  const loadDialogHubs = useCallback(async (cid) => {
    if (!cid) { setDialogHubs([]); return; }
    try {
      const { data } = await axios.get(`${API}/hubs`, { headers: { Authorization: `Bearer ${localStorage.getItem("tl_token")}`, "X-Company-Id": cid } });
      setDialogHubs(data);
    } catch (e) { console.error("Failed to load hubs for selected company", e); setDialogHubs([]); }
  }, []);
  const isAdmin = me?.role === "admin";

  const loadUsers = useCallback(async () => {
    if (!isAdmin) return;
    try { setUsers((await api.get("/auth/users")).data); } catch { /* non-admin */ }
  }, [isAdmin]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  const openAddUser = () => { const cid = companies[0]?.id || ""; setUEditing(null); setUf({ ...emptyUser, company_id: cid, hub_ids: [] }); setShowPw(false); loadDialogHubs(cid); setUOpen(true); };
  const openEditUser = (u) => { setUEditing(u.id); setUf({ name: u.name || "", email: u.email || "", password: "", role: u.role || "staff", company_id: u.company_id || "", hub_ids: u.hub_ids || [] }); loadDialogHubs(u.company_id); setUOpen(true); };
  const openReset = (u) => { setResetUser(u); setResetPw(""); setShowRpw(false); };
  const saveReset = async () => {
    if ((resetPw || "").length < 6) return toast.error("Password must be at least 6 characters");
    setSavingR(true);
    try { await api.put(`/auth/users/${resetUser.id}/password`, { password: resetPw }); toast.success(`Password reset for ${resetUser.name}`); setResetUser(null); setResetPw(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not reset password"); }
    finally { setSavingR(false); }
  };
  const toggleHub = (id) => setUf((p) => ({ ...p, hub_ids: p.hub_ids.includes(id) ? p.hub_ids.filter((x) => x !== id) : [...p.hub_ids, id] }));
  const saveUser = async () => {
    if (!uf.name.trim()) return toast.error("Name is required");
    if (uf.role !== "admin" && uf.hub_ids.length === 0) return toast.error("Select at least one authorised hub for this login");
    if (!uEditing) {
      if (!uf.email.trim()) return toast.error("Email is required");
      if ((uf.password || "").length < 6) return toast.error("Password must be at least 6 characters");
    }
    setSavingU(true);
    try {
      if (uEditing) {
        await api.put(`/auth/users/${uEditing}`, { name: uf.name, role: uf.role, hub_ids: uf.role === "admin" ? [] : uf.hub_ids });
        toast.success("Login updated");
      } else {
        await api.post("/auth/users", { ...uf, email: uf.email.trim().toLowerCase() });
        toast.success(`${uf.name} can now log in`);
      }
      setUOpen(false); setUf(emptyUser); setUEditing(null); loadUsers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save user"); }
    finally { setSavingU(false); }
  };
  const delUser = async (u) => {
    if (u.id === me?.id) return toast.error("You cannot delete your own account");
    if (!window.confirm(`Remove login for ${u.name} (${u.email})?`)) return;
    try { await api.delete(`/auth/users/${u.id}`); toast.success("Login removed"); loadUsers(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove user"); }
  };
  const compName = (id) => companies.find((c) => c.id === id)?.name || "—";

  return (
    <div>
      <PageIntro title="Settings" subtitle="Manage hubs, team logins and company configuration" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-card border border-border rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h3 className="font-display font-semibold flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" />Hubs / Locations ({hubs.length})</h3>
            <Button size="sm" data-testid="add-hub-button" onClick={openCreate}><Plus className="h-4 w-4 mr-1" />Add Hub</Button>
          </div>
          <div className="overflow-x-auto">
            {hubs.length === 0 ? <Empty /> : (
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase font-bold text-muted-foreground tracking-wider">
                  <tr><th className="py-3 px-4 text-left">Name</th><th className="py-3 px-4 text-left">Code</th><th className="py-3 px-4 text-left">Manager</th><th className="py-3 px-4 text-left">Location</th><th className="py-3 px-4 text-right">Action</th></tr>
                </thead>
                <tbody>
                  {hubs.map((h) => (
                    <tr key={h.id} className="border-t border-border/60 hover:bg-muted/30" data-testid={`hub-row-${h.id}`}>
                      <td className="py-3 px-4 font-medium">{h.name}</td>
                      <td className="py-3 px-4 font-mono">{h.code}</td>
                      <td className="py-3 px-4">{h.manager || "-"}</td>
                      <td className="py-3 px-4 text-muted-foreground">{h.location || "-"}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-3">
                          <button data-testid={`edit-hub-${h.id}`} onClick={() => openEdit(h)} className="text-muted-foreground hover:text-primary transition-colors" title="Edit hub"><Pencil className="h-4 w-4" /></button>
                          <button data-testid={`delete-hub-${h.id}`} onClick={() => del(h.id)} className="text-muted-foreground hover:text-rose-500 transition-colors" title="Remove hub"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl shadow-sm p-5 space-y-4">
          <h3 className="font-display font-semibold">Company Profile</h3>
          <Field label="Company Name"><Input defaultValue="Shree Krishna Enterprise" data-testid="company-name-input" /></Field>
          <Field label="GSTIN"><Input defaultValue="29AABCT1234F1Z5" data-testid="company-gstin-input" /></Field>
          <Field label="Contact Email"><Input defaultValue="info@shreekrishna.in" data-testid="company-email-input" /></Field>
          <Field label="Currency"><Input defaultValue="INR (₹)" disabled data-testid="company-currency-input" /></Field>
          <Button className="w-full" data-testid="save-company-button" onClick={() => toast.success("Company profile saved")}>Save Profile</Button>
        </div>
      </div>

      {isAdmin && (
        <div className="mt-6 bg-card border border-border rounded-xl shadow-sm overflow-hidden" data-testid="team-section">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h3 className="font-display font-semibold flex items-center gap-2"><Users className="h-4 w-4 text-primary" />Team &amp; Logins ({users.length})</h3>
            <Button size="sm" data-testid="add-user-button" onClick={openAddUser}><Plus className="h-4 w-4 mr-1" />Add Staff Login</Button>
          </div>
          <div className="overflow-x-auto">
            {users.length === 0 ? <Empty text="No team members yet" /> : (
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase font-bold text-muted-foreground tracking-wider">
                  <tr><th className="py-3 px-4 text-left">Name</th><th className="py-3 px-4 text-left">Email</th><th className="py-3 px-4 text-left">Role</th><th className="py-3 px-4 text-left">Company</th><th className="py-3 px-4 text-right">Action</th></tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-t border-border/60 hover:bg-muted/30" data-testid={`user-row-${u.id}`}>
                      <td className="py-3 px-4 font-medium">{u.name} {u.id === me?.id && <span className="text-xs text-muted-foreground">(you)</span>}</td>
                      <td className="py-3 px-4 font-mono text-xs">{u.email}</td>
                      <td className="py-3 px-4"><span className={roleBadge(u.role)}>{u.role}</span></td>
                      <td className="py-3 px-4 text-muted-foreground">{compName(u.company_id)}{u.role !== "admin" && <span className="ml-2 text-xs text-muted-foreground/70">· {(u.hub_ids || []).length} hub{(u.hub_ids || []).length === 1 ? "" : "s"}</span>}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-3">
                          <button data-testid={`edit-user-${u.id}`} onClick={() => openEditUser(u)} className="text-muted-foreground hover:text-primary transition-colors" title="Edit access"><Pencil className="h-4 w-4" /></button>
                          <button data-testid={`reset-user-${u.id}`} onClick={() => openReset(u)} className="text-muted-foreground hover:text-amber-500 transition-colors" title="Reset password"><KeyRound className="h-4 w-4" /></button>
                          <button data-testid={`delete-user-${u.id}`} disabled={u.id === me?.id} onClick={() => delUser(u)} className="text-muted-foreground hover:text-rose-500 transition-colors disabled:opacity-30" title="Remove login"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="hub-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Hub" : "Add Hub"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Hub Name"><Input data-testid="hub-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Bengaluru Hub" /></Field>
            <Field label="Code"><Input data-testid="hub-code-input" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="BLR" /></Field>
            <Field label="Manager"><Input data-testid="hub-manager-input" value={f.manager} onChange={(e) => setF({ ...f, manager: e.target.value })} /></Field>
            <Field label="Location"><Input data-testid="hub-location-input" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} /></Field>
          </div>
          <DialogFooter><Button data-testid="save-hub-button" onClick={submit}>{editing ? "Update Hub" : "Save Hub"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={uOpen} onOpenChange={(o) => { setUOpen(o); if (!o) setUEditing(null); }}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader><DialogTitle>{uEditing ? "Edit Login & Hub Access" : "Add Staff Login"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Field label="Full Name"><Input data-testid="user-name-input" value={uf.name} onChange={(e) => setUf({ ...uf, name: e.target.value })} placeholder="Ravi Kumar" /></Field></div>
            <div className="col-span-2"><Field label="Email (login ID)"><Input data-testid="user-email-input" value={uf.email} disabled={!!uEditing} onChange={(e) => setUf({ ...uf, email: e.target.value })} placeholder="ravi@company.com" /></Field></div>
            {!uEditing && (
              <div className="col-span-2"><Field label="Password">
                <div className="relative">
                  <Input data-testid="user-password-input" type={showPw ? "text" : "password"} value={uf.password} onChange={(e) => setUf({ ...uf, password: e.target.value })} placeholder="Min 6 characters" />
                  <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" data-testid="user-password-toggle">{showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                </div>
              </Field></div>
            )}
            <Field label="Role">
              <Select value={uf.role} onValueChange={(v) => setUf({ ...uf, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff (Dispatch &amp; Receive)</SelectItem>
                  <SelectItem value="manager">Manager (all except Settings)</SelectItem>
                  <SelectItem value="admin">Admin (full access)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Company">
              <Select value={uf.company_id} onValueChange={(v) => { setUf({ ...uf, company_id: v, hub_ids: [] }); loadDialogHubs(v); }} disabled={!!uEditing}>
                <SelectTrigger data-testid="user-company-select"><SelectValue placeholder="Select company" /></SelectTrigger>
                <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            {uf.role === "admin" ? (
              <div className="col-span-2 text-xs text-muted-foreground bg-primary/5 border border-primary/20 rounded-lg px-3 py-2">Admins have full access to all hubs.</div>
            ) : (
              <div className="col-span-2">
                <Label className="text-xs font-semibold text-muted-foreground">Authorised Hubs (this login can only access these)</Label>
                <div className="mt-2 grid grid-cols-2 gap-2" data-testid="user-hubs-picker">
                  {dialogHubs.length === 0 && <div className="text-xs text-muted-foreground col-span-2">No hubs in the selected company yet.</div>}
                  {dialogHubs.map((h) => (
                    <label key={h.id} className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg border cursor-pointer transition-colors ${uf.hub_ids.includes(h.id) ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-muted/40"}`} data-testid={`user-hub-${h.id}`}>
                      <input type="checkbox" className="accent-primary" checked={uf.hub_ids.includes(h.id)} onChange={() => toggleHub(h.id)} />
                      {h.name}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setUOpen(false); setUEditing(null); }}>Cancel</Button>
            <Button onClick={saveUser} disabled={savingU} data-testid="save-user-button">{savingU ? "Saving…" : (uEditing ? "Save Changes" : "Create Login")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetUser} onOpenChange={(o) => { if (!o) setResetUser(null); }}>
        <DialogContent className="max-w-md" data-testid="reset-dialog">
          <DialogHeader><DialogTitle>Reset Password</DialogTitle></DialogHeader>
          {resetUser && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">Set a new password for <span className="font-medium text-foreground">{resetUser.name}</span> <span className="font-mono text-xs">({resetUser.email})</span>.</div>
              <Field label="New Password">
                <div className="relative">
                  <Input data-testid="reset-password-input" type={showRpw ? "text" : "password"} value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="Min 6 characters" />
                  <button type="button" onClick={() => setShowRpw((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" data-testid="reset-password-toggle">{showRpw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                </div>
              </Field>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetUser(null)}>Cancel</Button>
            <Button onClick={saveReset} disabled={savingR} data-testid="reset-save-button">{savingR ? "Resetting…" : "Reset Password"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
